import { useCallback, useRef, useState } from 'react';
import { useAppStore } from '../../../../store/appStore';
import type { RunnerTutorialHint } from '../gameplayConstants';

function readIsFirstRunnerGame(): boolean {
  return (useAppStore.getState().playerEconomy.deliveryHistory?.length ?? 0) === 0;
}

export function useRunnerTutorial(): {
  isFirstRun: boolean;
  hint: RunnerTutorialHint | null;
  skipCountdown: boolean;
  dismiss: () => void;
  resetForNewAttempt: () => void;
  onObstacleHit: (livesAfterHit: number) => void;
  onBoosterCollected: () => void;
  onCollectibleCollected: () => void;
  /** @returns true if the second-chance hint was shown and should defer the minigame. */
  onLivesDepleted: () => boolean;
} {
  const isFirstRunRef = useRef(readIsFirstRunnerGame());
  const isFirstRun = isFirstRunRef.current;
  const [hint, setHint] = useState<RunnerTutorialHint | null>(
    isFirstRun ? 'controls' : null,
  );
  const hintRef = useRef(hint);
  hintRef.current = hint;
  const obstacleShownRef = useRef(false);
  const boosterShownRef = useRef(false);
  const collectibleShownRef = useRef(false);
  const secondChanceShownRef = useRef(false);

  const dismiss = useCallback(() => {
    hintRef.current = null;
    setHint(null);
  }, []);

  const resetForNewAttempt = useCallback(() => {
    obstacleShownRef.current = false;
    boosterShownRef.current = false;
    collectibleShownRef.current = false;
    secondChanceShownRef.current = false;
    const next: RunnerTutorialHint | null = isFirstRun ? 'controls' : null;
    hintRef.current = next;
    setHint(next);
  }, [isFirstRun]);

  const onObstacleHit = useCallback(
    (livesAfterHit: number) => {
      if (!isFirstRun || obstacleShownRef.current || hintRef.current != null) {
        return;
      }
      if (livesAfterHit <= 0) {
        return;
      }
      obstacleShownRef.current = true;
      hintRef.current = 'obstacle';
      setHint('obstacle');
    },
    [isFirstRun],
  );

  const onBoosterCollected = useCallback(() => {
    if (!isFirstRun || boosterShownRef.current || hintRef.current != null) {
      return;
    }
    boosterShownRef.current = true;
    hintRef.current = 'booster';
    setHint('booster');
  }, [isFirstRun]);

  const onCollectibleCollected = useCallback(() => {
    if (!isFirstRun || collectibleShownRef.current || hintRef.current != null) {
      return;
    }
    collectibleShownRef.current = true;
    hintRef.current = 'collectible';
    setHint('collectible');
  }, [isFirstRun]);

  const onLivesDepleted = useCallback(() => {
    if (!isFirstRun || secondChanceShownRef.current || hintRef.current != null) {
      return false;
    }
    secondChanceShownRef.current = true;
    hintRef.current = 'secondChance';
    setHint('secondChance');
    return true;
  }, [isFirstRun]);

  return {
    isFirstRun,
    hint,
    skipCountdown: isFirstRun,
    dismiss,
    resetForNewAttempt,
    onObstacleHit,
    onBoosterCollected,
    onCollectibleCollected,
    onLivesDepleted,
  };
}
